// Helmet: Dynamic page title
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import StyledComponent from 'styled-components';

import {
  Alert,
  AlertProvider,
  Footer,
  GuardedRoute,
  NavigationBar,
  NavigationBarProvider,
  NavList,
  PageController,
} from './components';
import { schemaFieldToFormInput } from './components/Form/utility';
import './css/App.css';
import './css/bootstrap.min.css';
import './css/themify-icons.css';
import { ApiManager, SiteManager } from './managers';
import {
  AirtableMap,
  Emails,
  Home,
  JobManager,
  Logout,
  ModelManager,
  NoMatch,
  Reports,
} from './pages';
import { CookieValues } from './types';

const AppContainer = StyledComponent.div`
  .app-wrapper
  {
    min-height: 100%;
    height: auto !important;
    height: 100%;
    background: #F5F5F5;
  }

  .pane-holder
  {
      margin-top: 0px;
      max-width:  100%;
      margin-top: 10px;
      display:    flex;
      flex-direction: row;
      padding-bottom: 80px;
      @media only screen and (max-width: 992px)
      {
        flex-direction: column;
      }
  }
  .left-pane
  {
      text-align: left;
      display: flex;
      flex-direction: column;

        width: 15%;
        float: left;
        margin-left: 1%;
  }

  .center-pane
  {
      display:flex;
      margin-left: 2%;
      margin-right: 2%;
      flex-direction:column;
      float: right;
      border-radius: 4px;

      @media only screen and (max-width: 992px) and (min-width: 601px)
      {
        margin-top: 2%;
        margin-left: auto;
        margin-right: auto;
      }
      @media only screen and (max-width: 600px)
      {
        width:100% !important;
        margin-left: 0%;
        margin-right: 0%;
      }
  }
  .center-pane-80
  {
    width: 80%;
  }
  .center-pane-100
  {
    width: 100%;
  }

  .full-width:
  {
    width: 100% !important;
  }

  text-align:         center;
  background-color:   #FFFFFF;
  width:              100%;
  position:           absolute !important;
  top:                0px;
  bottom:             0;

  @media only screen and (max-width: 992px)
  {
    position: relative;
  }
  `;

function App() {
  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
  const [pageComponents, setPageComponents] = useState<Map<any, any>>();

  useEffect(() => {
    ApiManager.Init(cookies.token);

    window.addEventListener('resize', () => {
      setWindowSize(window.innerWidth);
    });

    const initSiteManager = async () => {
      const siteManager = SiteManager.GetInstance();
      await siteManager.init(cookies.token);

      // Build page/form input map
      const pageComponents = new Map();
      const pageNames = siteManager.getPageNames();
      for (let i = 0; i < pageNames.length; i++) {
        const formInputs = [];
        const page = siteManager.getPageData(pageNames[i]);

        // Iterate components in page and pre-process them
        for (let i = 0; i < page.components.length; i++) {
          // Handle form
          if (page.components[i].type === 'form') {
            // Now iterate schema fields and convert to form inputs
            for (let j = 0; j < page.components[i].form.length; j++) {
              const formInput = await schemaFieldToFormInput(
                page.components[i].form[j],
                cookies.token,
              );
              formInputs.push(formInput);
            }

            page.components[i].form = formInputs;
          }
        }

        pageComponents.set(page.name, page.components);

        setPageComponents(pageComponents);
      }
    };

    initSiteManager();
  }, [cookies.token]);

  const isMobile = windowSize <= 768;
  const isLoggedIn = !!cookies.token;

  const apiManager = ApiManager.GetInstance();

  if (!apiManager) {
    return null;
  }

  return (
    <AppContainer>
      <Helmet>
        <title>Checkin</title>
      </Helmet>
      <AlertProvider>
        <Router>
          <NavigationBarProvider>
            <div className="app-wrapper">
              <NavigationBar isMobile={isMobile} />
              <Alert />
              <div className="pane-holder">
                {!isMobile && isLoggedIn && (
                  <div className="left-pane">
                    <NavList />
                  </div>
                )}
                <div className={`center-pane center-pane-${isLoggedIn ? '80' : '100'}`}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('login') ?? [])]}
                        />
                      }
                    />
                    <Route
                      path="/airtable"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <AirtableMap />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/login"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('login') ?? [])]}
                        />
                      }
                    />
                    <Route
                      path="/model-manager"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <ModelManager />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/jobs"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <JobManager />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Reports />
                        </GuardedRoute>
                      }
                    />
                    <Route
                      path="/emails"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Emails />
                        </GuardedRoute>
                      }
                    />
                    {false && (
                      <Route
                        path="/register"
                        element={
                          <PageController
                            pageComponents={[...(pageComponents?.get('register') ?? [])]}
                          />
                        }
                      />
                    )}
                    <Route
                      path="/home"
                      element={
                        <GuardedRoute authorizationTypes={['admin', 'customer']}>
                          <Home />
                        </GuardedRoute>
                      }
                    />
                    <Route path="/logout" element={<Logout />} />
                    <Route
                      path="/reset"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('reset') ?? [])]}
                        />
                      }
                    />
                    <Route
                      path="/confirm-reset"
                      element={
                        <PageController
                          pageComponents={[...(pageComponents?.get('confirm-reset') ?? [])]}
                        />
                      }
                    />
                    <Route element={<NoMatch />} />
                  </Routes>
                </div>
              </div>
            </div>
          </NavigationBarProvider>
        </Router>
      </AlertProvider>
      <Footer />
    </AppContainer>
  );
}

export default App;
